import { Box, CircularProgress, TextField, Theme, Typography, useMediaQuery } from "@material-ui/core";
import { ChangeEvent, FC, memo, MutableRefObject, useEffect, useState } from "react";

import ConfirmationPopover from "../../../components/ConfirmationPopover";
import { areEqual } from "../../../utils/generic";
import { CellManageMode } from "./types";
import SelectRange from "./SelectRange";
import useDebounce from "../../../hooks/useDebounce";
import { checkReservedSlots, getSlotsInRange } from "./utils/queries";
import useCurrentUser from "../../../hooks/useCurrentUser";

const AvailabilityToolBar: FC<AvailabilityToolBarProps> = ({
  rangeRef,
  onSubmit,
  loading,
  disableSubmitBtn,
  onSelectTime,
  defaultMinSlot,
  onHasReservedSlots,
}) => {
  const { user } = useCurrentUser();
  const [hasReservedSlots, setHasReservedSlots] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { setTempValue, tempValue, searchValue } = useDebounce({
    defaultValue: defaultMinSlot / 60,
  });
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const handleRangeChange = async (range: any) => {
    setHasReservedSlots(true);

    try {
      const hasReservedSlots = await checkReservedSlots(
        getSlotsInRange(user.id!, range.startDate, range.endDate),
        CellManageMode.overwrite
      );
      if (hasReservedSlots) {
        setErrorMessage("This range has some reserved slots");
        setHasReservedSlots(true);
        onHasReservedSlots(true);
        return;
      }
      onHasReservedSlots(false);
      setErrorMessage(null);
      setHasReservedSlots(false);
    } catch (ex: any) {
      console.log(ex.message);
      setErrorMessage(null);
      onHasReservedSlots(false);
      setHasReservedSlots(false);
      return;
    }

    if (range)
      rangeRef.current = {
        startDate: range.startDate,
        endDate: range.endDate,
      };
  };

  const handleChangeTime = (e: ChangeEvent<any>) => {
    const value = e.target.value;
    if (!value || value >= 0) {
      setTempValue(value);
    }
  };

  useEffect(() => {
    onSelectTime(searchValue ? searchValue * 60 : defaultMinSlot);
  }, [searchValue, onSelectTime, defaultMinSlot]);

  return (
    <Box display={isSm ? "" : "flex"} justifyContent="space-between" pb={2}>
      <Box>
        <SelectRange getRange={handleRangeChange} />
        {hasReservedSlots && errorMessage && (
          <Box mt={2}>
            <Typography variant="body2" color="error">
              {errorMessage}
            </Typography>
          </Box>
        )}
      </Box>

      <Box display="flex" justifyContent={isSm ? "space-between" : ""} mt={2}>
        <Box mx={isSm ? 0 : 2}>
          <TextField
            variant="outlined"
            size="small"
            label="Slot time in mintues"
            value={tempValue}
            type="number"
            onChange={handleChangeTime}
            disabled={hasReservedSlots}
            inputProps={{
              min: 1,
            }}
          />
        </Box>

        <ConfirmationPopover
          onConfirm={() => onSubmit(tempValue * 60)}
          message={`Are you sure you want to overwrite slots?`}
          subMessage={`overwrite will replace stored slots with selected ones`}
          buttonText="Save"
          buttonVariant="contained"
          buttonColor="primary"
          buttonSize="medium"
          disabled={disableSubmitBtn}
          buttonStartIcon={loading ? <CircularProgress size={16} style={{ color: "white" }} /> : null}
        />
      </Box>
    </Box>
  );
};

interface AvailabilityToolBarProps {
  rangeRef: MutableRefObject<any>;
  onSubmit: (time: number) => void;
  onSelectTime: (time: number) => void;
  loading: boolean;
  disableSubmitBtn: boolean;
  defaultMinSlot: number;
  onHasReservedSlots: (hasReservedSlots: boolean) => void;
}

export default memo(AvailabilityToolBar, areEqual);
